import { Transition, TransitionProps } from 'notistack';
import { cloneElement, ReactElement, Ref, RefObject, useRef } from 'react';

const TRANSITION_MS = 230;

interface ISnackbarTransitionProps extends TransitionProps {
  children: ReactElement<{ ref?: Ref<HTMLDivElement> }>;
  onEnter?: (node: HTMLElement, isAppearing: boolean) => void;
  onEntered?: (node: HTMLElement, isAppearing: boolean) => void;
  onExit?: (node: HTMLElement) => void;
  onExited?: (node: HTMLElement) => void;
}

export const SnackbarTransition = ({
  children,
  onEnter,
  onEntered,
  onExit,
  onExited,
  ...rest
}: ISnackbarTransitionProps) => {
  const nodeRef = useRef<HTMLDivElement>(null);
  const exitedFiredRef = useRef(false);

  const setStatus = (status: 'entering' | 'entered' | 'exiting' | 'exited') => {
    nodeRef.current?.setAttribute('data-snackbar-status', status);
  };

  return (
    <Transition
      {...rest}
      nodeRef={nodeRef as RefObject<HTMLDivElement>}
      timeout={TRANSITION_MS}
      onEnter={(node, isAppearing) => {
        exitedFiredRef.current = false;
        setStatus('entering');
        onEnter?.(node, isAppearing);
      }}
      onEntered={(node, isAppearing) => {
        setStatus('entered');
        onEntered?.(node, isAppearing);
      }}
      onExit={node => {
        setStatus('exiting');
        onExit?.(node);
        if (!exitedFiredRef.current) {
          exitedFiredRef.current = true;
          onExited?.(node);
        }
      }}
      onExited={node => {
        setStatus('exited');
        if (!exitedFiredRef.current) {
          exitedFiredRef.current = true;
          onExited?.(node);
        }
      }}
    >
      {() => cloneElement(children, { ref: nodeRef })}
    </Transition>
  );
};
